import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { AntispamPlatform } from '../types/models/AntispamPlatform';
import type { ApiResponseAntispamPlatformList } from '../types/api/ApiResponseAntispamPlatform';

interface UseAntispamPlatformsReturn {
  platforms: AntispamPlatform[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useAntispamPlatforms = (): UseAntispamPlatformsReturn => {
  const [platforms, setPlatforms] = useState<AntispamPlatform[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchPlatforms = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch('/emails/antispam-platforms');

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = (await response.json()) as ApiResponseAntispamPlatformList;
      // The antispam endpoints have no JSend `fail` arm and can return a
      // `status: 'error'` body at HTTP 200 — branch on status, not response.ok.
      if (data.status === 'success' && data.data?.antispam_platforms) {
        setPlatforms(data.data.antispam_platforms);
      } else {
        setError(data.message ?? 'Failed to load antispam platforms');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching antispam platforms');
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      fetchPlatforms();
    }
  }, [auth.isAuthenticated, fetchPlatforms]);

  return { platforms, loading, error, refetch: fetchPlatforms };
};
